import { useIntersection } from '@mantine/hooks';
import {
  Box,
  Card,
  CardContent,
  Checkbox,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { IconHelper } from 'components/generic/IconHelper';
import { IntersectionLoader } from 'components/generic/IntersectionLoader';
import { EditorialMiniMenu } from 'components/Minimenus/EditorialMiniMenu';
import { TMenuState, TSetMenuEvent } from 'components/Minimenus/MiniMenuSelector';
import { IconAndStatusSwitcher } from 'components/Search/components/elements/IconAndStatusSwitcher';
import SearchMediaPreview from 'components/Search/components/fields/generic/SearchMediaPreview';
import UserAvatar from 'components/UserAvatar/UserAvatar';
import { useCosmos } from 'providers/contexts/CosmosContext';
import { Fragment, memo, useCallback, useMemo, useRef, useState } from 'react';
import { useInfinitePaginationContext, useListContext } from 'react-admin';
import { useTranslation } from 'react-i18next';
import { bloxValues } from 'themes/constants';
import { MMM_DD_YYYY_C_12HHMM } from 'utility/constants';
import {
  cleanStringFromHtmlAndBloxInline,
  formatAssetString,
  formatDate,
  isAssetTypeEditorEnabled,
} from 'utility/functions';
import { getPrimaryBgColor, getSolidThemeGray } from 'utility/styles';
import { EditorialAssetSearchResultItem } from 'utility/types/dataProvider';
import { useSearchModalContext } from '../SearchModal';
import { checkSelectDisabled } from '../utilities';

/**
 * --------------------------------------------------------
 * Search Modal Card View - Editorial
 * - Renders results in card layout
 * --------------------------------------------------------
 */

export const SearchModalCardsFragmentEditorial = memo(() => {
  const { t } = useTranslation();
  const { resource, getRecordId } = useSearchModalContext();
  const { data, isFetching } = useListContext<EditorialAssetSearchResultItem>();
  const { fetchNextPage, isFetchingNextPage, hasNextPage } =
    useInfinitePaginationContext();

  const isBeingFetched = isFetchingNextPage || isFetching;

  const assetList: EditorialAssetSearchResultItem[] = useMemo(() => {
    if (!data) {
      return [];
    }
    return data;
  }, [data]);

  const { ref: ixRef, entry } = useIntersection({
    root: null,
    threshold: 0.5,
  });

  const strEndOfResults = t('blox.general.end_of_results');

  return (
    <Fragment>
      {assetList.map((item, index) => {
        const itemId = getRecordId(item, resource);
        const reactKey = `search-card-${itemId}`;
        return (
          <SearchModalEditorialCard
            key={reactKey}
            record={item}
          />
        );
      })}
      <IntersectionLoader
        hasNextPage={hasNextPage}
        isFetching={isBeingFetched}
        fetchNextPage={fetchNextPage}
        endOfResultsMessage={strEndOfResults}
        ixRef={ixRef}
        entry={entry}
        compact={true}
      />
    </Fragment>
  );
});
SearchModalCardsFragmentEditorial.displayName = 'SearchModalCardViewEditorial';

/**
 * --------------------------------------------------------
 * Search Modal - Editorial Cards
 * - Card items for editorial assets
 * --------------------------------------------------------
 */

type SearchModalEditorialCardProps = {
  record: EditorialAssetSearchResultItem;
};

const SearchModalEditorialCard = memo(({ record }: SearchModalEditorialCardProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { sessionConfig } = useCosmos();
  const siteTimeZone = sessionConfig?.time_zone;

  const {
    resource,
    setSelections,
    selections,
    selectedIds,
    dataGridApiRef,
    checkItemSelectDisabled,
    handleSelectItem,
    clearSelections,
    miniMenuConfig,
    getRecordId,
  } = useSearchModalContext();

  const cardRef = useRef<HTMLDivElement>(null);

  // State for right click menu
  const [menuEvent, setMenuEvent] = useState<TMenuState>(null);

  // hover states for styles
  const [cardHover, setCardHover] = useState<boolean>(false);

  const hasSelectionsOrHovered = selectedIds.length || cardHover;

  // Data / formatting
  const recId = getRecordId(record, resource);
  const reactKey = `search-card-item-${recId}`;
  const type = record?.type || '';
  const title = record?.title || '';
  const summary = record?.summary;
  const content = record?.content;
  const previews = record?.previews;
  const start_time = record?.start_time;
  const authors = record?.authors;
  const author = authors?.[0];
  const cleanSummary = cleanStringFromHtmlAndBloxInline(summary || content || '');
  const fmtStartTime = formatDate({
    date: start_time,
    shiftTz: siteTimeZone,
    format: MMM_DD_YYYY_C_12HHMM,
  });
  const previewImageUrl = previews?.preview?.url || previews?.thumbnail?.url;
  const typeFormatted = formatAssetString(type, t) || '';
  const editorEnabled = isAssetTypeEditorEnabled(type);

  const isCardSelected = useMemo(() => {
    if (!recId) return false;
    return selectedIds.includes(recId);
  }, [selectedIds, recId]);

  // Determine if selection/styling is disabled
  const isSelectDisabled = useMemo(() => {
    const isDisabled = checkSelectDisabled({
      resource: resource,
      record: record,
      checkItemSelectDisabled: checkItemSelectDisabled,
    });
    return isDisabled;
  }, [checkItemSelectDisabled, record, resource]);

  const handleMiniMenuClose = useCallback(() => {
    setMenuEvent(null);
  }, [setMenuEvent]);

  const handleClearSelections = useCallback(() => {
    clearSelections();
  }, [clearSelections]);

  // Handle context menu open and checks
  const handleContextMenu: TSetMenuEvent = useCallback(
    (event) => {
      event?.preventDefault();
      event?.stopPropagation();
      if (isSelectDisabled) {
        return;
      }
      // Open menu at position
      setMenuEvent(event);
    },
    [isSelectDisabled, setMenuEvent],
  );

  /**
   * Handle card click - item selection...
   * Delegate to context selection handler
   */
  const handleCardClick = useCallback(() => {
    handleSelectItem({ item: record });
  }, [handleSelectItem, record]);

  // prevent propagation of checkbox click to card click
  const onCheckboxClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  // Consider checkbox click as card click for selection
  const onCheckboxChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      e.stopPropagation();
      handleCardClick();
    },
    [handleCardClick],
  );

  const handleCardMouseEnter = useCallback(() => {
    setCardHover(true);
  }, []);

  const handleCardMouseLeave = useCallback(() => {
    setCardHover(false);
  }, []);

  // themes / constants
  const strNoPreview = t('blox.general.no_preview');
  const strEditingNotSupported = t('blox.general.edit_not_supported', {
    type: typeFormatted,
  });

  const strStartTime = t('blox.editorial.asset.start_time.alt_label');

  const isDarkMode = theme.palette.mode === 'dark';
  const baseBgColor = isDarkMode
    ? theme.palette.grey[900]
    : theme.palette.background.paper;
  const greyBgSolid = isDarkMode ? theme.palette.grey[800] : theme.palette.grey[100];
  const hoverDividerColor = isDarkMode
    ? theme.palette.primary.dark
    : theme.palette.primary.light;
  const bgHoverSolid = getPrimaryBgColor(theme, true);
  const dividerColor = theme.palette.divider;
  const transitionSx = theme.transitions.create([
    'background-color',
    'box-shadow',
    'border-color',
  ]);

  return (
    <Fragment key={reactKey}>
      <EditorialMiniMenu
        menuEvent={menuEvent}
        setMenuEvent={setMenuEvent}
        handleMiniMenuClose={handleMiniMenuClose}
        clearSelectedItems={handleClearSelections}
        setSelectedItems={setSelections}
        selectedItems={selections}
        raResource={resource}
        gridApiRef={dataGridApiRef}
        contextMenuItem={record ?? undefined}
        isContextItemDisabled={isSelectDisabled}
        menuConfig={miniMenuConfig}
      />
      <Card
        ref={cardRef} // for video hover play
        data-testid="search-modal-card"
        onContextMenu={handleContextMenu}
        onClick={handleCardClick}
        onMouseEnter={handleCardMouseEnter}
        onMouseLeave={handleCardMouseLeave}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          transition: transitionSx,
          outline: `2px solid ${
            isCardSelected ? theme.palette.primary.light : getSolidThemeGray(theme)
          }`,

          // Apply disabled styling (opacity and visual feedback)
          opacity: isSelectDisabled ? 0.5 : 1,
          color: isSelectDisabled
            ? theme.palette.text.secondary
            : theme.palette.text.primary,

          '& .MuiAvatar-root.MuiAvatar-circular': {
            opacity: 1,
            transition: 'opacity 0.2s',
          },
          '& .play-icon-overlay': {
            opacity: 1,
            transition: 'opacity 0.2s',
          },
          '& .video-expand-icon-overlay': {
            opacity: 0,
            transition: 'opacity 0.2s',
          },
          '&:hover': {
            backgroundColor: bgHoverSolid,
            cursor: 'default',
            '& #card-content': {
              backgroundColor: isSelectDisabled ? greyBgSolid : bgHoverSolid,
              boxShadow: isSelectDisabled
                ? `0px 0px 0px 1px ${dividerColor}`
                : `0px 0px 0px 1px ${hoverDividerColor}`,
              transition: transitionSx,
            },
            '& #card-mediatype-stack': {
              backgroundColor: isSelectDisabled ? greyBgSolid : bgHoverSolid,
              borderTop: isSelectDisabled
                ? `1px solid ${dividerColor}`
                : `1px solid ${hoverDividerColor}`,
              borderRight: isSelectDisabled
                ? `1px solid ${dividerColor}`
                : `1px solid ${hoverDividerColor}`,
              '&::before': {
                backgroundColor: isSelectDisabled ? greyBgSolid : bgHoverSolid,
                boxShadow: isSelectDisabled
                  ? `0px 1px 0px 0px ${greyBgSolid}`
                  : `0px 1px 0px 0px ${bgHoverSolid}`,
              },
              transition: transitionSx,
            },
            '& #card-media-box': {
              backgroundColor: greyBgSolid,
              transition: transitionSx,
            },
            '& .play-icon-overlay': {
              opacity: 0,
            },
            '& .video-expand-icon-overlay': {
              opacity: 1,
            },
          },
        }}
      >
        <Box
          id="card-media-box"
          data-testid="card-media-box"
          sx={{
            height: 230,
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            backgroundColor: greyBgSolid,
            transition: transitionSx,
          }}
        >
          <Checkbox
            id="checkbox-select-card"
            data-testid="checkbox-select-card"
            checked={isCardSelected}
            onChange={onCheckboxChange}
            onClick={onCheckboxClick}
            size="small"
            sx={{
              position: 'absolute',
              top: 8,
              left: 8,
              display: hasSelectionsOrHovered ? undefined : 'none',
              zIndex: 500,
              borderRadius: '50%',
              color: theme.palette.action.active,
              '&.MuiCheckbox-root': {
                backgroundColor: hasSelectionsOrHovered
                  ? 'rgba(144, 144, 144, 0.25)'
                  : undefined,
                '&.Mui-checked': {
                  color: theme.palette.primary.light,
                  backgroundColor: 'rgba(144, 144, 144, 0.25)',
                  '&:hover': {
                    backgroundColor: 'rgba(144, 144, 144, 0.25)',
                  },
                },
              },
            }}
          />

          {previewImageUrl ? (
            <SearchMediaPreview
              record={record}
              previewImageUrl={previewImageUrl}
              view="card"
              isHovered={cardHover}
            />
          ) : (
            <Stack
              id="no-preview-available-stack"
              data-testid="no-preview-available-stack"
              direction="column"
              sx={{
                alignItems: 'center',
              }}
            >
              <Typography
                sx={{
                  fontSize: bloxValues.regularFontSizeRem,
                  fontWeight: 400,
                  color: theme.palette.action.disabled,
                }}
              >
                {strNoPreview}
              </Typography>
              <IconHelper
                iconType={type}
                svgIconProps={{
                  fontSize: 'large',
                  style: {
                    fill: theme.palette.action.disabled,
                  },
                }}
              />
            </Stack>
          )}
          <UserAvatar
            user={author}
            size={40}
            placement="top"
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              boxShadow: theme.shadows[1],
            }}
          />
        </Box>
        <CardContent
          id="card-content"
          data-testid="card-content"
          sx={{
            '&.MuiCardContent-root': {
              p: 1,
              '&:last-child': {
                pb: 1,
              },
            },
            display: 'flex',
            flexDirection: 'column',
            flexGrow: 1,
            justifyContent: 'space-between',
            position: 'relative',
            // border, works better than actual border for the curved effect
            boxShadow: `0px 0px 0px 1px ${dividerColor}`,
            backgroundColor: baseBgColor,
            transition: transitionSx,
          }}
        >
          <Stack
            id="card-mediatype-stack"
            data-testid="card-mediatype-stack"
            direction="row"
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
              position: 'absolute',
              top: -28,
              left: 0,
              pr: '8px',
              pl: '7px',
              pb: '0px',
              pt: '4px',
              borderTopRightRadius: '5px',
              borderTop: `1px solid ${dividerColor}`,
              borderRight: `1px solid ${dividerColor}`,
              backgroundColor: baseBgColor,
              transition: transitionSx,
              // pseudo element paper color to mask the border(boxShadow)
              // on the card-content and 2px for subpixel rendering coverage
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 25,
                left: 0,
                width: '100%',
                height: '2px',
                backgroundColor: baseBgColor,
                boxShadow: `0px 1px 0px 0px ${baseBgColor}`,
                transition: transitionSx,
              },
            }}
          >
            <IconHelper
              iconType={type}
              svgIconProps={{
                fontSize: 'small',
                sx: {
                  zIndex: 1, // above the pseudo element
                },
              }}
            />
            <Typography
              sx={{
                fontSize: 'small',
                color: theme.palette.text.secondary,
                fontWeight: 400,
                textTransform: 'capitalize',
                pl: '2px',
              }}
            >
              {typeFormatted}
            </Typography>
          </Stack>
          <Stack
            id="card-text-stack"
            data-testid="card-text-stack"
            direction="column"
            sx={{
              gap: '10px',
            }}
          >
            <Box
              id="card-title-box"
              data-testid="card-title-box"
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 1 / 2,
              }}
            >
              <Typography
                sx={{
                  color: theme.palette.text.primary,
                  fontSize: bloxValues.xlFontSizeRem,
                  fontWeight: 500,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  wordBreak: 'normal',
                  display: '-webkit-box',
                  WebkitBoxOrient: 'vertical',
                  WebkitLineClamp: 2,
                }}
              >
                {title}
              </Typography>
            </Box>
            <Typography
              sx={{
                color: theme.palette.text.secondary,
                fontSize: bloxValues.regularFontSizeRem,
                fontWeight: 400,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                wordBreak: 'normal',
                display: '-webkit-box',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: 3,
              }}
            >
              {cleanSummary}
            </Typography>
          </Stack>
          <Stack
            id="card-footer-col"
            direction="column"
            sx={{
              pt: '10px',
              gap: '5px',
            }}
          >
            {!editorEnabled && (
              <Typography
                sx={{
                  color: isDarkMode ? theme.palette.error.dark : theme.palette.error.main,
                  fontSize: bloxValues.tinyFontSizeRem,
                  fontWeight: 400,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  wordBreak: 'normal',
                  display: '-webkit-box',
                  WebkitBoxOrient: 'vertical',
                }}
              >
                {strEditingNotSupported}
              </Typography>
            )}
            <Stack
              id="starttime-and-status-apart"
              data-testid="starttime-and-status-apart"
              direction="row"
              sx={{
                justifyContent: 'space-between',
              }}
            >
              <Tooltip
                id="date-tooltip"
                data-testid="date-tooltip"
                disableInteractive
                title={strStartTime}
                arrow={false}
                placement="top-start"
                enterDelay={200}
                slotProps={{
                  tooltip: {
                    sx: {
                      px: '6px',
                      py: '2px',
                    },
                  },
                  popper: {
                    popperOptions: {
                      modifiers: [
                        {
                          name: 'offset',
                          options: {
                            offset: [0, -12],
                          },
                        },
                      ],
                    },
                  },
                }}
              >
                <Typography
                  id="date-text"
                  data-testid="date-text"
                  sx={{
                    textTransform: 'capitalize',
                    fontSize: bloxValues.smallFontSizeRem,
                    color: theme.palette.text.secondary,
                  }}
                >
                  {fmtStartTime}
                </Typography>
              </Tooltip>
              <Typography
                id="status-text"
                data-testid="status-text"
                sx={{
                  textTransform: 'capitalize',
                  fontSize: bloxValues.smallFontSizeRem,
                  color: theme.palette.text.secondary,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1 / 2,
                }}
              >
                <IconAndStatusSwitcher
                  record={record}
                  resource={resource}
                />
              </Typography>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </Fragment>
  );
});
SearchModalEditorialCard.displayName = 'SearchModalEditorialCard';
